'use client';

import React, { useState, useEffect } from 'react';
import GuideCard from './GuideCard';
import CategoryFilter from './CategoryFilter';
import SearchBar from './SearchBar';
import FeaturedGuide from './FeaturedGuide';
import InteractiveTools from './InteractiveTools';
import EmergencyResources from './EmergencyResources';
import ExpertAuthors from './ExpertAuthors';
import { featuredGuide, allGuides, Guide } from '@/data/guides';
import Icon from '@/components/ui/AppIcon';
import { StaggerContainer, StaggerItem, FadeIn, SlideUp } from '@/components/ui/motion';

interface Category {
  id: string;
  name: string;
  icon: string;
  count: number;
}

interface Tool {
  id: string;
  title: string;
  description: string;
  icon: string;
  color: string;
}

interface EmergencyResource {
  id: string;
  title: string;
  description: string;
  icon: string;
}

interface Expert {
  id: string;
  name: string;
  credentials: string;
  specialization: string;
  avatar: string;
  avatarAlt: string;
  guidesCount: number;
  rating: number;
}

const CareGuidesInteractive = () => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [activeCategory, setActiveCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 6;

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  // Reset page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [activeCategory, searchQuery]);

  const categories: Category[] = [
    { id: 'all', name: 'All Guides', icon: 'Squares2X2Icon', count: 72 },
    { id: 'dogs', name: 'Dogs', icon: 'HeartIcon', count: 22 },
    { id: 'cats', name: 'Cats', icon: 'SparklesIcon', count: 18 },
    { id: 'birds', name: 'Birds', icon: 'MusicalNoteIcon', count: 12 },
    { id: 'reptiles', name: 'Reptiles', icon: 'FireIcon', count: 12 },
    { id: 'small-pets', name: 'Small Pets', icon: 'StarIcon', count: 12 },
    { id: 'fish-aquatics', name: 'Fish & Aquatics', icon: 'BeakerIcon', count: 5 }
  ];

  const tools: Tool[] = [
    {
      id: 'tool-1',
      title: 'Pet Food Calculator',
      description: 'Calculate precise daily food portions based on weight, age, and activity level',
      icon: 'CalculatorIcon',
      color: 'bg-primary'
    },
    {
      id: 'tool-2',
      title: 'Health Tracker',
      description: 'Monitor vaccinations, medications, and vet appointments in one place',
      icon: 'HeartIcon',
      color: 'bg-success'
    },
    {
      id: 'tool-3',
      title: 'Compatibility Quiz',
      description: 'Find the perfect pet species and breed for your lifestyle',
      icon: 'PuzzlePieceIcon',
      color: 'bg-accent'
    },
    {
      id: 'tool-4',
      title: 'Cost Estimator',
      description: 'Estimate lifetime costs of pet ownership including food, vet care, and supplies',
      icon: 'CurrencyDollarIcon',
      color: 'bg-warning'
    }
  ];

  const emergencyResources: EmergencyResource[] = [
    {
      id: 'emergency-1',
      title: 'Poisoning & Toxins',
      description: 'Immediate steps for suspected poisoning and toxic substance exposure',
      icon: 'ExclamationCircleIcon'
    },
    {
      id: 'emergency-2',
      title: 'Injury First Aid',
      description: 'Emergency response for wounds, fractures, and trauma situations',
      icon: 'BandageIcon'
    },
    {
      id: 'emergency-3',
      title: '24/7 Vet Hotline',
      description: 'Connect with emergency veterinarians for urgent consultations',
      icon: 'PhoneIcon'
    }
  ];

  const experts: Expert[] = [
    {
      id: 'expert-1',
      name: 'Dr. Sarah Mitchell',
      credentials: 'DVM, DACVB',
      specialization: 'Veterinary Behavior',
      avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_189601afd-1763296832715.png",
      avatarAlt: 'Professional photo of Caucasian female veterinarian with blonde hair in white coat',
      guidesCount: 24,
      rating: 4.9
    },
    {
      id: 'expert-2',
      name: 'Dr. Michael Chen',
      credentials: 'DVM, PhD',
      specialization: 'Nutrition & Wellness',
      avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1445acc88-1763298867979.png",
      avatarAlt: 'Professional headshot of Asian male veterinarian in white coat with stethoscope',
      guidesCount: 18,
      rating: 4.8
    },
    {
      id: 'expert-3',
      name: 'Dr. Emily Rodriguez',
      credentials: 'DVM, CABC',
      specialization: 'Feline Medicine',
      avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_11395fdb9-1763298869389.png",
      avatarAlt: 'Professional portrait of Hispanic female veterinarian with brown hair in medical scrubs',
      guidesCount: 16,
      rating: 4.9
    }
  ];

  const filteredGuides = isHydrated ?
    allGuides.filter((guide) => {
      const matchesCategory = activeCategory === 'all' ||
        (activeCategory === 'fish-aquatics' ? guide.category === 'Fish & Aquatics' : guide.category.toLowerCase() === activeCategory);
      const matchesSearch = guide.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        guide.description.toLowerCase().includes(searchQuery.toLowerCase());
      return matchesCategory && matchesSearch;
    }) :
    allGuides;

  const totalPages = Math.ceil(filteredGuides.length / itemsPerPage);
  const currentGuides = filteredGuides.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    // Optional: Scroll to top of grid
    const grid = document.getElementById('guides-grid');
    if (grid) grid.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  if (!isHydrated) {
    return (
      <div className="min-h-screen bg-background">
        <div className="w-full px-4 py-8 lg:px-8">
          <div className="animate-pulse space-y-8">
            <div className="h-64 bg-muted rounded-2xl"></div>
            <div className="grid lg:grid-cols-4 gap-6">
              <div className="h-96 bg-muted rounded-xl"></div>
              <div className="lg:col-span-3 grid md:grid-cols-2 gap-6">
                {[1, 2, 3, 4].map((i) =>
                  <div key={i} className="h-96 bg-muted rounded-xl"></div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="w-full px-4 py-8 lg:px-8">
        <div className="mb-8">
          <FadeIn>
            <FeaturedGuide guide={featuredGuide} />
          </FadeIn>
        </div>

        <div className="mb-8">
          <SearchBar searchQuery={searchQuery} onSearchChange={setSearchQuery} />
        </div>

        <div className="grid lg:grid-cols-4 gap-6">
          <div className="space-y-6">
            <SlideUp delay={0.1}>
              <CategoryFilter
                categories={categories}
                activeCategory={activeCategory}
                onCategoryChange={setActiveCategory}
              />
            </SlideUp>

            <SlideUp delay={0.2}>
              <InteractiveTools tools={tools} />
            </SlideUp>

            <SlideUp delay={0.3}>
              <EmergencyResources resources={emergencyResources} />
            </SlideUp>
          </div>

          <div className="lg:col-span-3">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-headline font-bold text-foreground">
                {activeCategory === 'all' ? 'All Care Guides' : `${categories.find((c) => c.id === activeCategory)?.name} Guides`}
              </h2>
              <span className="text-muted-foreground">
                {filteredGuides.length} {filteredGuides.length === 1 ? 'guide' : 'guides'}
              </span>
            </div>

            {filteredGuides.length > 0 ?
              <>
                <StaggerContainer id="guides-grid" className="grid md:grid-cols-2 gap-6 mb-8">
                  {currentGuides.map((guide) =>
                    <StaggerItem key={guide.id}>
                      <GuideCard guide={guide} />
                    </StaggerItem>
                  )}
                </StaggerContainer>

                {/* Pagination Controls */}
                {totalPages > 1 && (
                  <div className="flex justify-center items-center space-x-2 mt-12">
                    <button
                      onClick={() => handlePageChange(Math.max(1, currentPage - 1))}
                      disabled={currentPage === 1}
                      className="p-2 rounded-lg hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      aria-label="Previous Page"
                    >
                      <Icon name="ChevronLeftIcon" size={20} />
                    </button>

                    {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                      <button
                        key={page}
                        onClick={() => handlePageChange(page)}
                        className={`w-10 h-10 rounded-lg font-medium transition-colors ${currentPage === page
                          ? 'bg-primary text-primary-foreground'
                          : 'hover:bg-muted text-muted-foreground'
                          }`}
                      >
                        {page}
                      </button>
                    ))}

                    <button
                      onClick={() => handlePageChange(Math.min(totalPages, currentPage + 1))}
                      disabled={currentPage === totalPages}
                      className="p-2 rounded-lg hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      aria-label="Next Page"
                    >
                      <Icon name="ChevronRightIcon" size={20} />
                    </button>
                  </div>
                )}
              </> :
              <div className="text-center py-16">
                <p className="text-muted-foreground text-lg">
                  No guides found matching your search criteria.
                </p>
              </div>
            }

            <div className="mt-8">
              <FadeIn delay={0.2}>
                <ExpertAuthors experts={experts} />
              </FadeIn>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CareGuidesInteractive;